import { useMemo } from 'react';
import type { QuoteBid } from '../types';
import { formatPrice } from '../leadLabels';

/**
 * Insurer price-comparison cards.
 *
 * Modelled after consumer comparison sites: a bold price header, the coverage
 * packages included in the route (Qualla "riders"), the security level, and a
 * Hebrew breakdown of the policy terms (Qualla "additionalDetails"). Every value
 * is translated and formatted by type — nothing is shown as raw JSON.
 *
 * Qualla schema notes (learned from a live dump):
 *   - `path`         → Hebrew route name  ("הפניקס זהיר - מסלול באחריות")
 *   - `pathFullName` → internal English code ("CoverComprehensiveGarageAgr")
 *   - rich terms live under the nested `additionalDetails` object
 *   - coverage packages live under the `riders[]` array (`include: 1` = included)
 */

/** PDF links come back relative (e.g. "/pdf/fenix-zahir.pdf") → resolve against Qualla. */
const QUALLA_PDF_BASE = 'https://api.qualla.co.il';

type ValueKind = 'price' | 'percent' | 'percentText' | 'included' | 'ownership' | 'text';

interface AdField {
  label: string;
  kind: ValueKind;
  highlight?: boolean;
}

/** Hebrew dictionary for the nested `additionalDetails` terms, in display order. */
const AD_FIELDS: Record<string, AdField> = {
  thirdPartyLiabilityLimit: { label: "גבול אחריות צד ג'", kind: 'price', highlight: true },
  deductibleThirdParty: { label: "השתתפות עצמית בתביעה צד ג'", kind: 'price', highlight: true },
  deductibleArrangement: { label: 'השתתפות עצמית במוסך הסדר', kind: 'price' },
  // Usually a number ("2495") but occasionally a sentence ("תיקון רק במוסך הסדר…") →
  // the 'price' kind formats numbers as ₪ and passes free text through unchanged.
  deductibleWithoutArrangement: { label: 'השתתפות עצמית במוסך שלא בהסדר', kind: 'price' },
  deductibleInCase: { label: 'השתתפות עצמית בירידת ערך', kind: 'percentText' },
  legalDefense: { label: 'הגנה משפטית', kind: 'price' },
  legalDefenseDeductible: { label: 'השתתפות עצמית בהגנה משפטית', kind: 'price' },
  newForOld: { label: 'חדש תמורת ישן', kind: 'included' },
  carValue: { label: 'שווי הרכב', kind: 'price' },
  carOwnership: { label: 'בעלות הרכב', kind: 'ownership' },
  accessoriesValue: { label: 'שווי תוספות ואביזרים', kind: 'price' },
  accessoriesPrice: { label: 'עלות ביטוח תוספות', kind: 'price' },
  accessoriesProportion: { label: 'שיעור השתתפות בתוספות', kind: 'percent' },
  pricePerKm: { label: 'מחיר לק"מ', kind: 'price' },
  dailyCost: { label: 'עלות רכב חלופי ליום', kind: 'price' },
  dailyCostComments: { label: 'הערות רכב חלופי', kind: 'text' },
  proposalNum: { label: 'מספר הצעה', kind: 'text' },
};
const AD_ORDER = Object.keys(AD_FIELDS);

const OWNERSHIP_LABELS: Record<string, string> = {
  '1': 'פרטית',
  '2': 'חברה',
  '3': 'ליסינג',
  '4': 'השכרה',
};

/**
 * Fields where a value of 0 means "not applicable / not covered" (as opposed to
 * a deductible of 0, which is a real, meaningful benefit) → hidden when zero.
 */
const HIDE_WHEN_ZERO = new Set<string>([
  'thirdPartyLiabilityLimit',
  'legalDefense',
  'legalDefenseDeductible',
  'dailyCost',
  'dailyCostComments',
  'pricePerKm',
  'accessoriesValue',
  'accessoriesPrice',
  'carValue',
]);

interface Rider {
  name?: string;
  include?: number;
  recommendedPrice?: number;
  minPrice?: number;
}

function isEmpty(v: unknown): boolean {
  return v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v));
}

function toNum(v: unknown): number | undefined {
  if (typeof v === 'number') return Number.isNaN(v) ? undefined : v;
  if (typeof v === 'string') {
    const n = Number(v.replace(/[^\d.-]/g, ''));
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

/** Format one raw value according to its kind. Never returns raw JSON. */
function formatValue(kind: ValueKind, v: unknown, currency?: string): string {
  if (isEmpty(v)) return '—';
  switch (kind) {
    case 'price': {
      // Values may be numbers, numeric strings, or a full sentence ("תיקון רק במוסך…").
      const n = toNum(v);
      if (typeof v === 'string' && !/^\s*[\d,.\s]+\s*$/.test(v)) return v; // free text
      return n === undefined ? String(v) : formatPrice(n, currency);
    }
    case 'percent': {
      const n = toNum(v);
      return n === undefined ? String(v) : `${n}%`;
    }
    case 'percentText': {
      const n = toNum(v);
      return n === undefined ? String(v) : `${n}% משווי הרכב`;
    }
    case 'included':
      return v === false || v === 0 || v === '0' || v === 'false' ? 'לא כלול' : 'כלול';
    case 'ownership':
      return OWNERSHIP_LABELS[String(v)] ?? String(v);
    case 'text':
    default:
      if (typeof v === 'boolean') return v ? 'כן' : 'לא';
      if (typeof v === 'number') return v.toLocaleString('he-IL');
      return String(v);
  }
}

function absolutise(link: unknown): string | undefined {
  if (typeof link !== 'string' || !link) return undefined;
  if (/^https?:\/\//i.test(link)) return link;
  if (link.startsWith('/')) return `${QUALLA_PDF_BASE}${link}`;
  return undefined;
}

/** Best policy-terms PDF: prefer an already-absolute link (e.g. S3) over a relative one. */
function pickPdf(ad: Record<string, unknown>): string | undefined {
  const candidates = [ad.policyPrinciplesLink, ad.productPageLink];
  const abs = candidates.find((c) => typeof c === 'string' && /^https?:\/\//i.test(c));
  return absolutise(abs ?? candidates.find((c) => typeof c === 'string' && c));
}

/** Collapse the "----" filler and excess whitespace Qualla puts in protection notes. */
function cleanText(v: unknown): string {
  return String(v)
    .replace(/[-–—_]{2,}/g, ' — ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function isZero(v: unknown): boolean {
  return v === 0 || v === '0' || (typeof v === 'string' && Number(v.replace(/[^\d.-]/g, '')) === 0);
}

interface CoverageEntry {
  key: string;
  label: string;
  value: string;
  highlight: boolean;
  wide: boolean;
}

/** A single insurer card. */
function QuoteCard({
  bid,
  index,
  cheapest,
  currency,
}: {
  bid: QuoteBid;
  index: number;
  cheapest: boolean;
  currency?: string;
}) {
  const raw = bid.raw ?? {};
  const ad = (raw.additionalDetails as Record<string, unknown> | undefined) ?? {};
  const riders = (raw.riders as Rider[] | undefined) ?? [];

  const total = bid.totalPrice ?? bid.price;
  const routeName = bid.path || bid.branch; // path = Hebrew route name
  const pdf = pickPdf(ad);

  // Handling fee (דמי טיפול): part of the total not covered by מקיף + חובה.
  const handlingFee =
    bid.calculatedPrice != null && bid.dutyPrice != null && total != null
      ? Math.max(0, Math.round(total - bid.calculatedPrice - bid.dutyPrice))
      : undefined;

  // Coverage packages: those included in the price vs. optional add-ons.
  const includedRiders = riders.filter((r) => r.include && r.name);
  const optionalRiders = riders.filter((r) => !r.include && r.name);

  // Security / immobiliser level (מיגון).
  const protection = useMemo(() => {
    const desc = ad.protectionDescription;
    const code = ad.protectionCode;
    if (isEmpty(desc) && isEmpty(code)) return undefined;
    if (!isEmpty(desc)) return cleanText(desc);
    return `קוד ${code}`;
  }, [ad]);

  // Policy terms → translated coverage rows (from the nested additionalDetails).
  const coverages = useMemo<CoverageEntry[]>(() => {
    const entries: CoverageEntry[] = [];
    for (const key of AD_ORDER) {
      const v = ad[key];
      if (isEmpty(v)) continue;
      if (HIDE_WHEN_ZERO.has(key) && isZero(v)) continue;
      // Only surface accessory terms when the policy actually insures accessories.
      if (key.startsWith('accessories') && isEmpty(ad.accessoriesValue)) continue;
      const def = AD_FIELDS[key];
      const value = formatValue(def.kind, v, currency);
      entries.push({
        key,
        label: def.label,
        value,
        highlight: Boolean(def.highlight),
        wide: value.length > 32,
      });
    }
    return entries;
  }, [ad, currency]);

  return (
    <div className={`quote-card ${cheapest ? 'cheapest' : ''}`}>
      <div className="quote-card-head">
        <div className="quote-card-company">
          <span className="quote-card-rank">{index + 1}</span>
          <div>
            <div className="quote-card-name">
              {bid.company || bid.companyEn || 'חברת ביטוח'}
              {cheapest && <span className="quote-tag best">הכי משתלם</span>}
              {bid.isGreen && <span className="quote-tag green">מסלול ירוק</span>}
            </div>
            {routeName && <div className="quote-card-path">{routeName}</div>}
          </div>
        </div>

        <div className="quote-card-price">
          <div className="quote-card-total">{formatPrice(total, currency)}</div>
          <div className="quote-card-breakdown">
            {bid.calculatedPrice != null && <span>מקיף {formatPrice(bid.calculatedPrice, currency)}</span>}
            {bid.dutyPrice != null && <span>חובה {formatPrice(bid.dutyPrice, currency)}</span>}
            {handlingFee ? <span>דמי טיפול {formatPrice(handlingFee, currency)}</span> : null}
          </div>
        </div>
      </div>

      {/* Included coverage packages (riders) — the "כולל …" bullets. */}
      {includedRiders.length > 0 && (
        <div className="quote-card-included">
          {includedRiders.map((r, i) => (
            <span className="quote-included-chip" key={i}>
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
              {r.name}
            </span>
          ))}
        </div>
      )}

      {protection && (
        <div className="quote-card-protection">
          <span className="quote-cov-label">רמת מיגון</span>
          <span className="quote-cov-value">{protection}</span>
        </div>
      )}

      {/* Policy terms table — always visible, tabular, Hebrew. */}
      {coverages.length > 0 && (
        <div className="quote-card-coverages">
          {coverages.map((c) => (
            <div className={`quote-cov ${c.highlight ? 'hl' : ''} ${c.wide ? 'wide' : ''}`} key={c.key}>
              <span className="quote-cov-label">{c.label}</span>
              <span className="quote-cov-value">{c.value}</span>
            </div>
          ))}
        </div>
      )}

      {optionalRiders.length > 0 && (
        <div className="quote-card-optional">
          <span className="quote-optional-title">ניתן להוסיף בתוספת תשלום:</span>
          {optionalRiders.map((r, i) => (
            <span className="quote-optional-item" key={i}>
              {r.name}
              {r.recommendedPrice ? ` · ${formatPrice(r.recommendedPrice, currency)}` : ''}
            </span>
          ))}
        </div>
      )}

      {pdf && (
        <div className="quote-card-links">
          <a className="quote-link" href={pdf} target="_blank" rel="noreferrer">
            תנאי הפוליסה המלאים
          </a>
        </div>
      )}
    </div>
  );
}

/**
 * Renders all insurer bids as comparison cards, sorted cheapest → most
 * expensive, with the cheapest tagged "הכי משתלם".
 */
export function QuoteCards({ bids, currency }: { bids: QuoteBid[]; currency?: string }) {
  const sorted = useMemo(
    () => [...bids].sort((a, b) => (a.totalPrice ?? a.price) - (b.totalPrice ?? b.price)),
    [bids],
  );

  return (
    <div className="quote-cards">
      {sorted.map((b, i) => (
        <QuoteCard
          key={`${b.company}-${b.pathFullName ?? b.path ?? ''}-${i}`}
          bid={b}
          index={i}
          cheapest={i === 0}
          currency={currency}
        />
      ))}
    </div>
  );
}
